import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import {
    ApiErrorCode,
    BaseContainer,
    DialogConfig,
    DialogRef,
    ErrorResponse,
    MultiFormSelectOptions,
    NotificationType,
    Toast
} from '@stylehaven/components';
import { NotificationItem } from '../../../entities/notifications/notification-item.entity';
import { SavedQuery } from '../../../entities/saved-queries/saved-query.entity';
import { CustomValidators } from '../../../helpers';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { GroupId } from '../../../record/entities/metadata.entity';
import { SavedQueriesSandboxService } from '../../sandbox/saved-queries.sandbox';

@Component({
    standalone: false,
    selector: 'digihaven-save-query-dialog',
    templateUrl: './save-query-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SaveQueryDialogComponent extends BaseContainer implements OnInit, OnDestroy {
    readonly ErrorResponse = ErrorResponse;

    readonly saveLoading$ = new LoadingBehaviorSubject();
    readonly errorNotification$ = new BehaviorSubject<NotificationItem | undefined>(undefined);

    saveQueryForm!: UntypedFormGroup;
    groups$!: Observable<MultiFormSelectOptions>;

    canSaveSearch$!: Observable<boolean>;

    constructor(
        private dialogConfig: DialogConfig<string>,
        private dialogRef: DialogRef<SavedQuery>,
        private readonly sandbox: SavedQueriesSandboxService,
        private readonly formBuilder: UntypedFormBuilder
    ) {
        super();
    }

    ngOnInit(): void {
        this.initForm();

        this.initGroupOptions();

        this.canSaveSearch$ = combineLatest([
            this.saveQueryForm.statusChanges.pipe(map(status => status === 'VALID')),
            this.saveLoading$
        ]).pipe(map(([isValid, isLoading]) => isValid && !isLoading));
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.saveLoading$.complete();
    }

    private initForm() {
        this.saveQueryForm = this.formBuilder.group({
            name: [null, [Validators.required, CustomValidators.notBlank]],
            groups: [[]]
        });
    }

    private initGroupOptions() {
        this.groups$ = this.sandbox.getGroupOptions();
    }

    save(): void {
        this.beforeSave();

        const saveToast = this.sandbox.addSavingToast();

        this.saveNewQuery()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(
                savedQuery => this.afterSaveSuccess(saveToast, savedQuery),
                errorResponse => this.afterSaveFailure(errorResponse, saveToast)
            );
    }

    close(): void {
        this.dialogRef.close();
    }

    private beforeSave() {
        this.saveLoading$.startLoading();
        this.resetErrorNotification();
    }

    private afterSaveSuccess(toast: Toast, savedQuery: SavedQuery) {
        this.saveLoading$.stopLoading();
        this.resetErrorNotification();

        this.dialogRef.close(savedQuery);

        this.sandbox.updateSavingToastSuccess(
            toast.id,
            '/saved-queries',
            this.translateService.instant('components.saved-queries.new-saved-query-dialog.toast-link-text')
        );
    }

    private afterSaveFailure(errorResponse: HttpErrorResponse, toast: Toast) {
        this.saveLoading$.stopLoading();

        let errorMessageToken = this.translateService.instant(
            'components.saved-queries.new-saved-query-dialog.error.default'
        );
        if (errorResponse.error.Code === ApiErrorCode.EDUPLICATE) {
            errorMessageToken = this.translateService.instant(
                'components.saved-queries.new-saved-query-dialog.error.duplicate-name'
            );
        }

        this.setErrorNotification(errorMessageToken);
        this.sandbox.updateSavingToastFailure(toast.id);
    }

    private setErrorNotification(message: string) {
        this.errorNotification$.next({ message, type: NotificationType.Error, icon: 'delete' });
    }

    private resetErrorNotification() {
        this.errorNotification$.next(undefined);
    }

    private saveNewQuery(): Observable<SavedQuery> {
        const name = this.nameControl.value;
        const groups: GroupId[] = this.groupsControl.value || [];
        const query: string = this.dialogConfig.data ?? '';

        return this.sandbox.saveQuery({ Name: name, Groups: groups, Query: query });
    }

    get nameControl(): UntypedFormControl {
        return this.saveQueryForm.get('name') as UntypedFormControl;
    }

    get groupsControl(): UntypedFormControl {
        return this.saveQueryForm.get('groups') as UntypedFormControl;
    }
}
